/**
 * Download library — Storefront API data source.
 *
 * Pages through published products, reads docs.* file metafields, dedupes by
 * file URL, and renders cards into the existing download-library markup.
 * No hosting, no rebuild script: a Plytix sync that lands on products is enough.
 */
(function () {
  'use strict';

  var API_VERSION = '2025-01';
  var PAGE_SIZE = 50;

  var PREFERRED_CATEGORY_HANDLES = [
    'commercial-indoor-lighting',
    'commercial-outdoor-lighting',
  ];

  var EXCLUDED_COLLECTION_HANDLES = {
    'new-arrivals': true,
    'featured-products': true,
    'gkoled': true,
    'gradeone': true,
    'naturaled': true,
    'e-e': true,
    'all': true,
    'frontpage': true,
  };

  var LEADING_QUALIFIERS = /^(LPP_|US_|SELECTABLE_)+/i;

  var PRODUCTS_QUERY =
    'query DownloadLibraryProducts($cursor: String, $pageSize: Int!) {\n' +
    '  products(first: $pageSize, after: $cursor) {\n' +
    '    pageInfo { hasNextPage endCursor }\n' +
    '    nodes {\n' +
    '      handle\n' +
    '      title\n' +
    '      vendor\n' +
    '      availableForSale\n' +
    '      spec: metafield(namespace: "docs", key: "spec_sheet") {\n' +
    '        reference {\n' +
    '          ... on GenericFile { url mimeType originalFileSize alt }\n' +
    '        }\n' +
    '      }\n' +
    '      install: metafield(namespace: "docs", key: "install_sheet") {\n' +
    '        reference {\n' +
    '          ... on GenericFile { url mimeType originalFileSize alt }\n' +
    '        }\n' +
    '      }\n' +
    '      ies: metafield(namespace: "docs", key: "ies_files") {\n' +
    '        references(first: 25) {\n' +
    '          nodes {\n' +
    '            ... on GenericFile { url mimeType originalFileSize alt }\n' +
    '          }\n' +
    '        }\n' +
    '      }\n' +
    '      lineDrawing: metafield(namespace: "docs", key: "line_drawing_1") {\n' +
    '        reference {\n' +
    '          ... on GenericFile { url mimeType originalFileSize alt }\n' +
    '          ... on MediaImage { image { url } }\n' +
    '        }\n' +
    '      }\n' +
    '      iesCone: metafield(namespace: "docs", key: "ies_cone_file") {\n' +
    '        reference {\n' +
    '          ... on GenericFile { url mimeType originalFileSize alt }\n' +
    '          ... on MediaImage { image { url } }\n' +
    '        }\n' +
    '      }\n' +
    '      applicationImages: metafield(namespace: "docs", key: "application_images") {\n' +
    '        reference {\n' +
    '          ... on GenericFile { url mimeType originalFileSize alt }\n' +
    '          ... on MediaImage { image { url } }\n' +
    '        }\n' +
    '        references(first: 25) {\n' +
    '          nodes {\n' +
    '            ... on GenericFile { url mimeType originalFileSize alt }\n' +
    '            ... on MediaImage { image { url } }\n' +
    '          }\n' +
    '        }\n' +
    '      }\n' +
    '      collections(first: 20) {\n' +
    '        nodes { handle title }\n' +
    '      }\n' +
    '    }\n' +
    '  }\n' +
    '}';

  function handleize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, '&#96;');
  }

  function filenameFromUrl(url) {
    if (!url) return '';
    try {
      var path = url.split('?')[0];
      var parts = path.split('/');
      return decodeURIComponent(parts[parts.length - 1] || '');
    } catch (e) {
      return '';
    }
  }

  function decodePlytixTokens(name) {
    return String(name || '')
      .replace(/_20/g, ' ')
      .replace(/_28/g, '(')
      .replace(/_29/g, ')')
      .replace(/%20/gi, ' ');
  }

  function formatFileSize(bytes) {
    var n = Number(bytes);
    if (!n || n < 0 || !isFinite(n)) return '';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) {
      var kb = n / 1024;
      return (kb >= 100 ? Math.round(kb) : kb.toFixed(kb >= 10 ? 0 : 1)) + ' KB';
    }
    var mb = n / (1024 * 1024);
    return (mb >= 10 ? mb.toFixed(1) : mb.toFixed(1)) + ' MB';
  }

  function titleFromFile(url, vendor, badge) {
    var raw = filenameFromUrl(url);
    var name = decodePlytixTokens(raw).replace(/\.[^.]+$/, '');
    name = name.replace(LEADING_QUALIFIERS, '');
    var readable = name.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();

    if (!readable || /^\d/.test(readable)) {
      if (vendor) return vendor + (badge ? ' ' + badge : '');
      return badge || 'Download';
    }

    var first = readable.split(' ')[0];
    if (/[A-Za-z]/.test(first) && /\d/.test(first)) {
      return first + (badge ? ' ' + badge : '');
    }

    // Drop redundant trailing type words already covered by the badge.
    if (badge) {
      var badgeWords = badge.toLowerCase().split(/\s+/);
      var tokens = readable.split(/\s+/);
      while (tokens.length > 1) {
        var tail = tokens[tokens.length - 1].toLowerCase();
        if (badgeWords.indexOf(tail) !== -1 || tail === 'sheet' || tail === 'guide' || tail === 'install') {
          tokens.pop();
        } else {
          break;
        }
      }
      readable = tokens.join(' ');
    }

    return readable || badge || 'Download';
  }

  function categoryFromCollections(collections) {
    var nodes = (collections && collections.nodes) || [];
    var preferred = null;
    var fallback = null;

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (!node || !node.handle) continue;
      var handle = String(node.handle).toLowerCase();
      if (EXCLUDED_COLLECTION_HANDLES[handle]) continue;

      if (PREFERRED_CATEGORY_HANDLES.indexOf(handle) !== -1) {
        preferred = node.title || node.handle;
        break;
      }
      if (!fallback) fallback = node.title || node.handle;
    }

    return preferred || fallback || '';
  }

  function docKindMeta(kind) {
    if (kind === 'spec') {
      return { badge: 'Spec Sheet', filterType: 'Spec Sheets' };
    }
    if (kind === 'install') {
      return { badge: 'Install Guide', filterType: 'Install Guides' };
    }
    if (kind === 'ies') {
      return { badge: 'IES File', filterType: 'IES Files' };
    }
    if (kind === 'lineDrawing') {
      return { badge: 'Line Drawing', filterType: 'Line Drawings' };
    }
    if (kind === 'iesCone') {
      return { badge: 'IES Cone', filterType: 'IES Cone Diagrams' };
    }
    if (kind === 'applicationImages') {
      return { badge: 'Application Image', filterType: 'Application Images' };
    }
    return { badge: 'Document', filterType: 'Documents' };
  }

  function fileIconSvg() {
    return (
      '<svg width="38" height="38" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M6 2H14L20 8V22H6V2Z" stroke="currentColor" stroke-width="1.8" fill="none"/>' +
      '<path d="M14 2V8H20" stroke="currentColor" stroke-width="1.8"/>' +
      '</svg>'
    );
  }

  function downloadIconSvg() {
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<path d="M14 11.3333V13C14 13.5522 13.5523 14 13 14H3C2.44771 14 2 13.5522 2 13V11.3333" stroke="#1F2933" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M5 7.66667L8 10.6667L11 7.66667M8 10V2" stroke="#1F2933" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>'
    );
  }

  function collectFromProduct(product, byUrl) {
    if (!product) return;

    var category = categoryFromCollections(product.collections);
    var vendor = product.vendor || '';

    function addFile(file, kind) {
      if (!file) return;
      // Line drawings, IES cones and application images come through as MediaImage
      // rather than GenericFile, which carries its url one level down under `image`.
      var url = file.url || (file.image && file.image.url);
      if (!url) return;
      if (byUrl[url]) return;

      var meta = docKindMeta(kind);
      byUrl[url] = {
        url: url,
        title: titleFromFile(url, vendor, meta.badge),
        badge: meta.badge,
        filterType: meta.filterType,
        filterHandle: handleize(meta.filterType),
        category: category,
        size: formatFileSize(file.originalFileSize),
        filename: filenameFromUrl(url) || 'download',
        mimeType: file.mimeType || '',
      };
    }

    if (product.spec && product.spec.reference) {
      addFile(product.spec.reference, 'spec');
    }
    if (product.install && product.install.reference) {
      addFile(product.install.reference, 'install');
    }
    if (product.ies && product.ies.references && product.ies.references.nodes) {
      var iesNodes = product.ies.references.nodes;
      for (var i = 0; i < iesNodes.length; i++) {
        addFile(iesNodes[i], 'ies');
      }
    }
    if (product.lineDrawing && product.lineDrawing.reference) {
      addFile(product.lineDrawing.reference, 'lineDrawing');
    }
    if (product.iesCone && product.iesCone.reference) {
      addFile(product.iesCone.reference, 'iesCone');
    }
    // application_images is a list on some records and a single reference on others,
    // so both shapes are read rather than assuming one.
    if (product.applicationImages) {
      if (product.applicationImages.reference) {
        addFile(product.applicationImages.reference, 'applicationImages');
      }
      if (product.applicationImages.references && product.applicationImages.references.nodes) {
        var appNodes = product.applicationImages.references.nodes;
        for (var j = 0; j < appNodes.length; j++) {
          addFile(appNodes[j], 'applicationImages');
        }
      }
    }
  }

  function DownloadLibrary(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id') || '';
    this.shop = root.getAttribute('data-shop') || '';
    this.token = root.getAttribute('data-storefront-token') || '';
    this.layout = root.getAttribute('data-layout') || 'two-column';
    this.oneColumn = this.layout === 'one-column';

    this.grid = root.querySelector('[data-download-grid]');
    this.filtersEl = root.querySelector('[data-download-filters]');
    this.counter = root.querySelector('[data-download-count]');
    this.statusEl = root.querySelector('[data-download-status]');
    this.emptyEl = root.querySelector('[data-download-empty]');
    this.loadingEl = root.querySelector('[data-download-loading]');

    this.docs = [];
    this.activeFilter = 'all';
    this.loading = false;
    this.abortController = null;
  }

  DownloadLibrary.prototype.endpoint = function () {
    return 'https://' + this.shop + '/api/' + API_VERSION + '/graphql.json';
  };

  DownloadLibrary.prototype.setStatus = function (message, isError) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
      this.statusEl.classList.remove('is-error');
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
    this.statusEl.classList.toggle('is-error', !!isError);
  };

  DownloadLibrary.prototype.setLoading = function (on) {
    this.loading = on;
    if (this.loadingEl) this.loadingEl.hidden = !on;
    if (this.filtersEl) {
      this.filtersEl.setAttribute('aria-busy', on ? 'true' : 'false');
      this.filtersEl.classList.toggle('is-loading', on);
    }
  };

  DownloadLibrary.prototype.graphql = function (query, variables) {
    var self = this;
    return fetch(self.endpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': self.token,
      },
      body: JSON.stringify({ query: query, variables: variables || {} }),
      signal: self.abortController ? self.abortController.signal : undefined,
    }).then(function (res) {
      if (!res.ok) {
        throw new Error('Storefront API HTTP ' + res.status);
      }
      return res.json();
    }).then(function (json) {
      if (json.errors && json.errors.length) {
        throw new Error(json.errors[0].message || 'Storefront API error');
      }
      return json.data;
    });
  };

  DownloadLibrary.prototype.fetchAllDocuments = function () {
    var self = this;
    var byUrl = Object.create(null);
    var cursor = null;
    var safety = 0;

    function nextPage() {
      safety += 1;
      if (safety > 40) {
        return Promise.reject(new Error('Stopped after too many product pages'));
      }

      return self
        .graphql(PRODUCTS_QUERY, { cursor: cursor, pageSize: PAGE_SIZE })
        .then(function (data) {
          var connection = data && data.products;
          if (!connection) return;

          var nodes = connection.nodes || [];
          for (var i = 0; i < nodes.length; i++) {
            collectFromProduct(nodes[i], byUrl);
          }

          if (connection.pageInfo && connection.pageInfo.hasNextPage) {
            cursor = connection.pageInfo.endCursor;
            return nextPage();
          }
        });
    }

    return nextPage().then(function () {
      var docs = [];
      for (var url in byUrl) {
        if (Object.prototype.hasOwnProperty.call(byUrl, url)) {
          docs.push(byUrl[url]);
        }
      }
      docs.sort(function (a, b) {
        return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
      });
      return docs;
    });
  };

  DownloadLibrary.prototype.renderFilters = function () {
    if (!this.filtersEl) return;

    var seen = Object.create(null);
    var types = [];
    for (var i = 0; i < this.docs.length; i++) {
      var ft = this.docs[i].filterType;
      var fh = this.docs[i].filterHandle;
      if (!ft || seen[fh]) continue;
      seen[fh] = true;
      types.push({ label: ft, handle: fh });
    }
    types.sort(function (a, b) {
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });

    var html =
      '<button type="button" class="filter-btn active" data-filter="all">All Files</button>';
    for (var t = 0; t < types.length; t++) {
      html +=
        '<button type="button" class="filter-btn" data-filter="' +
        escapeAttr(types[t].handle) +
        '">' +
        escapeHtml(types[t].label) +
        '</button>';
    }

    // Preserve the label row if present as first child structure — rebuild only buttons after label.
    var label = this.filtersEl.querySelector('.filter-label');
    var labelHtml = label ? label.outerHTML : '';
    this.filtersEl.innerHTML = labelHtml + html;

    var self = this;
    var buttons = this.filtersEl.querySelectorAll('.filter-btn');
    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        buttons.forEach(function (btn) {
          btn.classList.remove('active');
        });
        button.classList.add('active');
        self.activeFilter = button.getAttribute('data-filter') || 'all';
        self.applyFilter();
      });
    });
  };

  DownloadLibrary.prototype.cardHtml = function (doc) {
    var one = this.oneColumn;
    var metaHtml;

    if (one) {
      metaHtml =
        '<div class="download-card__meta one-card-meta">' +
        (doc.badge
          ? '<span class="download-badge-one">' + escapeHtml(doc.badge) + '</span>'
          : '') +
        (doc.badge && doc.size ? ' • ' : '') +
        (doc.size ? '<span class="download-size">' + escapeHtml(doc.size) + '</span>' : '') +
        '</div>';
    } else {
      metaHtml =
        '<div class="download-card__meta">' +
        (doc.badge ? '<span class="download-badge">' + escapeHtml(doc.badge) + '</span>' : '') +
        (doc.category
          ? '<span class="download-category">' + escapeHtml(doc.category) + '</span>'
          : '') +
        '</div>';
    }

    return (
      '<div class="download-card" data-filter="' +
      escapeAttr(doc.filterHandle) +
      '">' +
      '<div class="download-card__icon' +
      (one ? ' one-card_icon' : '') +
      '">' +
      fileIconSvg() +
      '</div>' +
      '<div class="download-card__content">' +
      '<h4 class="download-card__title">' +
      escapeHtml(doc.title) +
      '</h4>' +
      metaHtml +
      '</div>' +
      '<div class="download-card__button">' +
      '<a href="' +
      escapeAttr(doc.url) +
      '" class="download-btn" target="_blank" rel="noopener noreferrer" download="' +
      escapeAttr(doc.filename) +
      '">' +
      downloadIconSvg() +
      ' Download</a>' +
      '</div>' +
      '</div>'
    );
  };

  DownloadLibrary.prototype.renderCards = function () {
    if (!this.grid) return;

    if (!this.docs.length) {
      this.grid.innerHTML = '';
      if (this.emptyEl) {
        this.emptyEl.hidden = false;
        var p = this.emptyEl.querySelector('p');
        if (p) p.textContent = 'No download files were found on published products.';
      }
      this.updateCounter(0);
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;

    var html = '';
    for (var i = 0; i < this.docs.length; i++) {
      html += this.cardHtml(this.docs[i]);
    }
    this.grid.innerHTML = html;
    this.applyFilter();
  };

  DownloadLibrary.prototype.applyFilter = function () {
    if (!this.grid) return;
    var filter = this.activeFilter || 'all';
    var cards = this.grid.querySelectorAll('.download-card');
    var visible = 0;

    cards.forEach(function (card) {
      var type = card.getAttribute('data-filter') || '';
      var show = filter === 'all' || type === filter;
      card.classList.toggle('is-hidden', !show);
      if (show) visible += 1;
    });

    if (this.emptyEl) {
      this.emptyEl.hidden = visible !== 0;
      if (visible === 0 && this.docs.length) {
        var p = this.emptyEl.querySelector('p');
        if (p) p.textContent = 'No downloads match this filter.';
      }
    }

    this.updateCounter(visible);
  };

  DownloadLibrary.prototype.updateCounter = function (count) {
    if (!this.counter) return;
    this.counter.textContent = count + (count === 1 ? ' Item' : ' Items');
  };

  DownloadLibrary.prototype.init = function () {
    var self = this;

    if (!this.shop || !this.token) {
      this.setLoading(false);
      this.setStatus('Storefront access is not configured for this section.', true);
      if (this.emptyEl) {
        this.emptyEl.hidden = false;
        var p = this.emptyEl.querySelector('p');
        if (p) {
          p.textContent =
            'Add the Headless public Storefront access token in the section settings.';
        }
      }
      this.updateCounter(0);
      return;
    }

    this.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    this.setLoading(true);
    this.setStatus('Loading downloads…', false);
    this.updateCounter(0);
    if (this.emptyEl) this.emptyEl.hidden = true;
    if (this.grid) this.grid.innerHTML = '';

    this.fetchAllDocuments()
      .then(function (docs) {
        self.docs = docs;
        self.setLoading(false);
        self.setStatus('');
        self.renderFilters();
        self.renderCards();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        console.warn('[download-library]', err);
        self.setLoading(false);
        self.setStatus(
          'Could not load downloads. Refresh the page or try again later.',
          true
        );
        if (self.emptyEl) {
          self.emptyEl.hidden = false;
          var p = self.emptyEl.querySelector('p');
          if (p) p.textContent = 'Downloads could not be loaded from the catalog.';
        }
        self.updateCounter(0);
      });
  };

  function boot() {
    var roots = document.querySelectorAll('[data-download-library-storefront]');
    roots.forEach(function (root) {
      if (root.__downloadLibraryBooted) return;
      root.__downloadLibraryBooted = true;
      new DownloadLibrary(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
